import React, { useEffect, KeyboardEvent, useState } from "react";
import { connect } from "react-redux";
import { IApplicationState } from './../../../../../~store/models/ApplicationState';
import { IMailingItemInfoProps } from "./~types/MailingItemInfoProps";

import './MailingItemInfo.css';
import { Button } from "reactstrap";
import { mailingItemInfoSelector, mailingRecipientsSelector } from "src/app/mailing/~store/selector";
import { useLocation, useNavigate } from "react-router-dom";
import { getMailingItemInfo, saveMailing, getRecipients, deleteRecipientsFromState, changeSearchRecipientsType, getAllBotsForMailing, initMailingInfo } from "src/app/mailing/~store/actions/actions";
import { useTranslation } from 'react-i18next';
import CreateMailingInfo from "./CreateMailingInfo/CreateMailingInfo";
import MailingRecipientsContactList from "./MailingRecipients/MailingRecipientsContacts/MailingRecipientsContactList";
import MailingRecipienstChatList from "./MailingRecipients/MailingRecipienstChats/MailingRecipienstChatList";
import { SearchRecipientsType } from "src/app/mailing/~store/models/enums/SearchRecipientsType";
import { showErrorToastr } from "src/app/shared/helpers/toastr-helper/ToastrHelper";
import { MailingType } from "src/app/mailing/~store/models/enums/MailingType";
import { MailingStatus } from "src/app/mailing/~store/models/enums/MailingStatus";
import { SendingType } from "./CreateMailingInfo/~types/enums/SendingType";
import { getIsUrlValid } from "../../../../shared/helpers/url-helper/UrlHelper";
import MailingRecipientsContactsWithVariablesList from "./MailingRecipients/MailingRecipienstContactsWithVariables/MailingRecipientsContactsWithVariablesList";

const MailingItemInfo = (props: ReturnType<typeof mapStateToProps> & IMailingItemInfoProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => { return () => { props.initMailingInfo(); } }, []);

    useEffect(() => {
        props.deleteRecipientsFromState();
        let mailingId = 0;
        let idFromUrl;
        if(window.location.pathname.includes('/mailing/edit-mailing/')) {
            idFromUrl = window.location.pathname.split('/')[window.location.pathname.split('/').length - 1];
            mailingId = +idFromUrl;
        }
        if (isNaN(mailingId)) {
            showErrorToastr(t("mailing.mailingReceivingByIdNotFoundMessage", { mailingId: idFromUrl }));
            navigate("/mailing");
            return;
        }
        props.getAllBotsForMailing();
        props.getMailingItemInfo(mailingId, () => navigate("/mailing"));
    }, [location]);

    const isAbleToSave = (): boolean => {
        if ( props.mailingItemInfo.mailingType === MailingType.message && (!props.mailingItemInfo.text || props.mailingItemInfo.text.trim().length === 0) &&
            !props.mailingItemInfo.image && !props.mailingItemInfo.pictureId
        ) {
            showErrorToastr(t('mailing.canNotSaveEmptyMessageError'));
            return false;
        } else if (props.mailingItemInfo.sendingType === SendingType.SendCascade && props.mailingItemInfo.channelsIds?.length <= 0 ) {
            showErrorToastr(t('mailing.canNotSendCascadeMailingWithoutSelectedChannels'));
            return false;
        } else if (!props.mailingItemInfo.name || props.mailingItemInfo.name.trim().length === 0) {
            showErrorToastr(t('mailing.canNotSaveEmptyNameError'));
            return false;
        } else if (props.mailingItemInfo.buttonUrl && !getIsUrlValid(props.mailingItemInfo.buttonUrl)) {
            showErrorToastr(t('mailing.canNotSaveWrongLinkUrlError'));
            return false;
        } else if (props.mailingItemInfo.buttonUrl && props.mailingItemInfo.buttonUrl.length > 0 && !props.mailingItemInfo.buttonText) {
            showErrorToastr(t('mailing.canSaveLinkWithoutLinkTextError'));
            return false;
        } else if (props.mailingItemInfo.mailingType === MailingType.bot && !props.mailingItemInfo.botStartParams.sb) {
            showErrorToastr(t('mailing.canNotSaveWithoutBotError'));
            return false;
        }
        else return true
    }

    const _onSaveButtonClick = async () => {
        if (isAbleToSave()) {
            setIsSaving(true);
            try {
                await props.saveMailing(() => navigate(-1));
            }
            finally {
                setIsSaving(false);
            }
        }
    }

    const handleKeyUpSearchTypeTab = (
      e: KeyboardEvent<HTMLSpanElement>,
      type: SearchRecipientsType
    ) => e.key === "Enter" && props.changeSearchRecipientsType(type);
    
    return(
        <div className="mailing-item-info">
            <CreateMailingInfo />
            <div className="mailing-item-info__channels-block">
                <div className="mailing-item-info__save-block">
                    <Button disabled={isSaving || props.mailingItemInfo.status !== MailingStatus.pending || 
                        props.mailingItemInfo.searchRecipientsType === SearchRecipientsType.inContactsWithVariables} 
                        onClick={_onSaveButtonClick}>
                            {t('common.save')}
                    </Button>
                </div>
            </div>
            <div className="mailing-recipients">
                <div className="search-type-tabs-wrapper">
                    <span 
                        className={props.mailingItemInfo.searchRecipientsType === SearchRecipientsType.inContacts ? "active" : ""}
                        onClick={() => {props.changeSearchRecipientsType(SearchRecipientsType.inContacts)}}
                        tabIndex={0}
                        onKeyUp={(e) => handleKeyUpSearchTypeTab(e, SearchRecipientsType.inContacts)}
                    >
                        {t('common.contacts')}
                    </span>
                    <span 
                        className={props.mailingItemInfo.searchRecipientsType === SearchRecipientsType.inChats ? "active" : ""}
                        onClick={() => {props.changeSearchRecipientsType(SearchRecipientsType.inChats)}}
                        tabIndex={0}
                        onKeyUp={(e) => handleKeyUpSearchTypeTab(e, SearchRecipientsType.inChats)}
                    >
                        {t('common.chats')}
                    </span>
                    <span 
                        className={props.mailingItemInfo.searchRecipientsType === SearchRecipientsType.inContactsWithVariables ? "active" : ""}
                        onClick={() => {props.changeSearchRecipientsType(SearchRecipientsType.inContactsWithVariables)}}
                        tabIndex={0}
                        onKeyUp={(e) => handleKeyUpSearchTypeTab(e, SearchRecipientsType.inContactsWithVariables)}
                    >
                        {t('mailing.contactsWithTemplates')}
                    </span>
                </div>
                {
                    props.mailingItemInfo.searchRecipientsType === SearchRecipientsType.inContacts && 
                        <MailingRecipientsContactList />
                }
                {
                    props.mailingItemInfo.searchRecipientsType === SearchRecipientsType.inChats && 
                        <MailingRecipienstChatList />
                }
                {
                    props.mailingItemInfo.searchRecipientsType === SearchRecipientsType.inContactsWithVariables && 
                        <MailingRecipientsContactsWithVariablesList />
                }
            </div>
        </div>
    );
}

const mapStateToProps = (state: IApplicationState) => {
    return {
        mailingItemInfo: mailingItemInfoSelector(state)
    };
};

const mapDispatchToProps = (dispatch: any): IMailingItemInfoProps => {
    return {
        getMailingItemInfo: (mailingId: number, onGoBackToMailings: () => void) => dispatch(getMailingItemInfo(mailingId, onGoBackToMailings)),
        getAllBotsForMailing: () => dispatch(getAllBotsForMailing()),
        saveMailing: (onGoBack: () => void) => dispatch(saveMailing(onGoBack)),
        deleteRecipientsFromState: () => dispatch(deleteRecipientsFromState()),
        changeSearchRecipientsType: (searchRecipientsType: SearchRecipientsType) => dispatch(changeSearchRecipientsType(searchRecipientsType)),
        initMailingInfo: () => dispatch(initMailingInfo())
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(MailingItemInfo);